import { useEventData } from "../DataProvider";
import { PanelWrapper } from "../PanelWrapper";
import { useTranslation } from "../../../i18n/useTranslation";
import { Header } from "./Header";
import { MarketSlip } from "./MarketSlip";
import { useEffect, useRef, useState } from "react";

interface PreRacePanelProps {
  showPanel: boolean;
}

export function PreRacePanel({ showPanel }: PreRacePanelProps) {
  const { t } = useTranslation();
  const { competitorsData } = useEventData();
  const [isCompactView, setIsCompactView] = useState(false);
  const [isSpecificContainer, setIsSpecificContainer] = useState(false); // Add this
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if we need to use compact view based on container height
  useEffect(() => {
    if (!containerRef.current) return;

    const checkSize = () => {
      const container = containerRef.current;
      if (container) {
        // Check container height against content needs
        const containerHeight = container.clientHeight;
        const containerWidth = container.clientWidth;
        
        // Check if we're in the specific 882x389 container (with some tolerance)
        // Check if we're in a small container that needs the fix
const isSpecific = containerWidth <= 900 && containerHeight <= 420;
        setIsSpecificContainer(isSpecific);
        
        const itemHeight = 65; // Approximate height of each MarketSlip
        const headerHeight = 60; // Approximate height of Header
        const padding = 40; // Total padding
        
        // Calculate how many items would fit in normal mode
        const availableHeight = containerHeight - headerHeight - padding;
        const fittableItems = Math.floor(availableHeight / itemHeight);
        
        // If we can't fit all competitors, enable compact view
        setIsCompactView(fittableItems < competitorsData.length);
      }
    };

    // Initial check
    checkSize();
    
    // Add resize observer for dynamic adjustments
    const resizeObserver = new ResizeObserver(checkSize);
    resizeObserver.observe(containerRef.current);
    
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [competitorsData.length, showPanel]);

  return (
    <PanelWrapper showPanel={showPanel}>
      <div 
        ref={containerRef}
        className={`flex items-start w-full bg-secondary bg-opacity-80 ${
          isSpecificContainer ? 'min-h-full' : 'h-full'
        }`}
      >
        <div className={`flex flex-col w-full h-full gap-2 p-2 
          ${isCompactView ? 'gap-0.5 py-0.5' : 'md:gap-6 md:p-6 lg:p-10'} lg:h-full`}>
          <Header />
          <div className="flex-1 w-full overflow-hidden">
            <div className={`flex flex-col flex-1 gap-1 h-full overflow-y-auto
              ${isCompactView ? 'gap-0.5' : 'md:gap-2'}`}>
              {competitorsData.map((c) => (
                <MarketSlip 
                  key={c.id} 
                  competitorData={c} 
                  compact={isCompactView}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
}